import { createContext, Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { Modal } from '../../Modal';
import { GlobalSettingsKeys } from '../../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../../Settings/GlobalSettingsProvider';
import { BaseDialogLayout } from './BaseDialogLayout';
import useHandleDialogOpenState from './useHandleDialogOpenState';
import bundle from '../NovaComponents-Dialog-gui-icu.properties';
import { jsx as _jsx } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const ParentChildRelContext = /*#__PURE__*/createContext(undefined);
const ParentChildRelProvider = ParentChildRelContext.Provider;

// Component to handle the dialog close state. This is specifically added to invoke the onClose prop
// after the the exit animation is executed.
const DialogCloseHandle = props => {
  const [, setVal] = useState(0);
  const useEffectCalled = useRef(false);
  const unmountTobeCalled = useRef(false);
  const closeRef = useRef(props.onClose);
  closeRef.current = props.onClose;
  if (useEffectCalled.current) {
    unmountTobeCalled.current = true;
  }
  useEffect(() => {
    useEffectCalled.current = true;
    setVal(v => v + 1);
    return () => {
      if (unmountTobeCalled.current) closeRef.current?.();
    };
  }, []);
  return /*#__PURE__*/_jsx(Fragment, {
    children: props.children
  });
};
export const BaseDialog = ({
  draggable = true,
  ...props
}) => {
  // workaround for NOVACOMMONS-1083
  // TODO This may be redundant now with NOVACOMMONS-2291, not sure I have time to test.
  useTextLocalizer();
  const isModal = props.isModal ?? true;
  const {
    isOpen,
    onOpen,
    onClose,
    ...restProps
  } = props;

  // The onclose prop is not passed through since the inovcation of close prop
  // is handled through DialogCloseHandle component.
  const {
    dialogRef
  } = useHandleDialogOpenState({
    onOpen,
    isOpen
  });
  const selfRegistry = useRef(new Set()).current;
  const parentRegistry = useContext(ParentChildRelContext);
  const origDismissActionHandle = props.dismissAction?.onPress;
  const origFirstActionHandle = props.firstAction?.onPress;
  const origSecondActionHandle = props.secondAction?.onPress;
  const proxyDismissHandle = useCallback(() => {
    selfRegistry.forEach(handler => handler?.apply(null));
    origDismissActionHandle?.apply(null);
  }, [selfRegistry, origDismissActionHandle]);
  const proxyFirstActionHandle = useCallback(() => {
    selfRegistry.forEach(handler => handler?.apply(null));
    origFirstActionHandle?.apply(null);
  }, [selfRegistry, origFirstActionHandle]);
  const proxySecondActionHandle = useCallback(() => {
    selfRegistry.forEach(handler => handler?.apply(null));
    origSecondActionHandle?.apply(null);
  }, [selfRegistry, origSecondActionHandle]);
  const dialogLayoutProps = {
    ...restProps,
    ...(restProps.dismissAction && restProps.dismissAction.onPress && {
      dismissAction: {
        ...restProps.dismissAction,
        onPress: proxyDismissHandle
      }
    }),
    ...(restProps.firstAction && restProps.firstAction.onPress && {
      firstAction: {
        ...restProps.firstAction,
        onPress: proxyFirstActionHandle
      }
    }),
    ...(restProps.secondAction && restProps.secondAction.onPress && {
      secondAction: {
        ...restProps.secondAction,
        onPress: proxySecondActionHandle
      }
    })
  };
  const hideAnimation = props.hideAnimation ?? false;
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true' && !hideAnimation;
  useEffect(() => {
    if (!isModal) {
      parentRegistry?.add(proxyDismissHandle);
      return () => {
        parentRegistry?.delete(proxyDismissHandle);
      };
    }
  }, [isModal, parentRegistry, proxyDismissHandle]);
  return /*#__PURE__*/_jsx(Modal, {
    id: props.id,
    isOpen: props.isOpen,
    showBlockLayer: isModal,
    focusLockProps: props.focusLockProps,
    onContextMenu: props.onContextMenu,
    testId: postfixTestId(props.testId, 'modal'),
    hideAnimation: !shouldPlayAnimation,
    children: /*#__PURE__*/_jsx(DialogCloseHandle, {
      onClose: props.onClose,
      children: /*#__PURE__*/_jsx(ParentChildRelProvider, {
        value: selfRegistry,
        children: props.renderLayout ? props.renderLayout() : /*#__PURE__*/_jsx(BaseDialogLayout, {
          ref: dialogRef,
          isOpen: props.isOpen,
          isModal: isModal,
          ...dialogLayoutProps,
          hideAnimation: !shouldPlayAnimation,
          draggable: draggable
        })
      })
    })
  });
};
//# sourceMappingURL=BaseDialog.js.map